import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HiClock, HiLocationMarker, HiArrowRight } from 'react-icons/hi';
import { FaCarSide } from 'react-icons/fa';
import ChildDots from '../common/ChildDots';
import {
  computeUserLogistics,
  groupLogisticsByDate,
  formatLogisticsDateHeader,
} from '@coparent/shared/src/utils/logisticsUtils';

const ROLE_META = {
  dropoff: { label: 'Drop off', className: 'bg-parent1-50 text-parent1-600' },
  pickup: { label: 'Pick up', className: 'bg-parent2-50 text-parent2-600' },
  both: { label: 'Drop off & Pick up', className: 'bg-shared-50 text-shared-600' },
};

const LogisticsCard = ({
  activities,
  custodyMap,
  parentIds,
  userId,
  rangeStart,
  rangeEnd,
  childrenMap,
  today,
  onEntryClick,
}) => {
  const entries = useMemo(
    () =>
      computeUserLogistics({
        activities,
        custodyMap,
        parentIds,
        userId,
        rangeStart,
        rangeEnd,
      }),
    [activities, custodyMap, parentIds, userId, rangeStart, rangeEnd]
  );

  const grouped = useMemo(() => groupLogisticsByDate(entries), [entries]);

  return (
    <div className="card p-6 mb-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-bold text-ink-900 flex items-center gap-2.5 text-[0.9375rem]">
          <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
            <FaCarSide className="text-amber-600 text-sm" />
          </div>
          Your Logistics
        </h2>
        <Link
          to="/calendar"
          className="text-xs text-ink-400 hover:text-ink-700 flex items-center gap-1 font-semibold transition-colors"
        >
          Calendar <HiArrowRight className="text-[0.625rem]" />
        </Link>
      </div>
      <p className="text-[0.6875rem] font-bold text-ink-400 uppercase tracking-wider mb-4">
        Next 7 days
      </p>

      {entries.length === 0 ? (
        <p className="text-center py-5 text-ink-300 text-sm font-medium">
          No drop-offs or pick-ups this week
        </p>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ date, items }) => (
            <div key={date}>
              <h3 className="text-[0.6875rem] font-bold text-ink-400 uppercase tracking-wider mb-2">
                {formatLogisticsDateHeader(date, today)}
              </h3>
              <div className="space-y-2">
                {items.map((e) => {
                  const meta = ROLE_META[e.role];
                  return (
                    <button
                      key={`${e.activityId}-${date}-${e.role}`}
                      type="button"
                      onClick={() => onEntryClick?.(e)}
                      className="w-full text-left flex items-start gap-3 p-3 rounded-xl bg-surface-50 hover:bg-surface-100 transition-colors"
                    >
                      <div
                        className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: e.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-ink-900 text-sm">{e.activityName}</span>
                          <span
                            className={`text-[0.625rem] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${meta.className}`}
                          >
                            {meta.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-ink-500 flex-wrap">
                          {e.time && (
                            <span className="flex items-center gap-1 font-medium">
                              <HiClock className="text-ink-300 text-[0.625rem]" />
                              {formatTime(e.time)}
                              {e.endTime ? ` – ${formatTime(e.endTime)}` : ''}
                            </span>
                          )}
                          {e.location && (
                            <span className="flex items-center gap-1 font-medium">
                              <HiLocationMarker className="text-ink-300 text-[0.625rem]" />
                              {e.location}
                            </span>
                          )}
                          {e.childIds?.length > 0 && (
                            <ChildDots childIds={e.childIds} childrenMap={childrenMap} size="xs" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatTime = (hhmm) => {
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return hhmm;
  const mer = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${mer}`;
};

export default LogisticsCard;
